/**
 * A file's name, as the portal shows it everywhere (Files, a review's list, the markup page, the timeline).
 *
 * A name can be only an id a device, a site or the browser made up: Abaya's floor plan came in as
 * "e1ecf41c-ce38-48df-8efd-32f0c16a9ce8.png" (found fixing P-26). One rule says so (`fileCalled`), and one names such a
 * file from where it went as it comes in (`nameAsItComes`; Jason, option A: "Name it as it comes in: a file whose name
 * is only an id gets one from where it went, e.g. 'Floor plan development design review, picture 1', renamable in Files
 * as any file is"). A name that says something is kept as it came.
 */

/**
 * A name that is only an id: a uuid, a long hash (hex letters and digits), or a pasted picture's "image.png". A name of
 * digits and dashes alone (a day, a time) still says when, so it is kept.
 */
const MADE_UP = /^(?:[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}|(?=.*\d)(?=.*[a-f])[0-9a-f_-]{16,}|image(?:\s?\(\d+\))?)$/i;

/** A file's name, when it says something; null for one only made up (`MADE_UP`), which is never shown as a name. */
export function fileCalled(name: string | null | undefined): string | null {
  const stem = (name ?? '').trim().replace(/\.[a-z0-9]{2,5}$/i, '').trim();
  return stem && !MADE_UP.test(stem) ? name!.trim() : null;
}

/** The pictures the portal can show (a web-sized copy is made of each), so a picture in words; a drawing format whose type only says image is a file. */
const PICTURE = /^image\/(jpeg|png|webp|gif|tiff|avif|heic|heif)$/;

export function isPicture(contentType: string | null | undefined): boolean {
  return PICTURE.test(contentType ?? '');
}

/** The design board, as a file put on it is named. */
export const BOARD_PLACE = 'Design board';

/** A review iteration, as a file dropped into it is named: the review's title, and past the first, its iteration. */
export function reviewPlace(title: string, iteration: number): string {
  return iteration > 1 ? `${title}, iteration ${iteration}` : title;
}

/**
 * A file's name as it comes in: kept when it says something; else "<where it went>, picture n" (or "file n"), n the
 * next of its kind there (`count` already there), never a name another of the project's files has (`taken`).
 */
export function nameAsItComes(name: string, to: { where: string; picture: boolean; count: number; taken: Iterable<string> }): string {
  const kept = fileCalled(name);
  if (kept) return kept;
  const taken = new Set([...to.taken].map((t) => t.trim().toLowerCase()));
  const named = (n: number) => `${to.where}, ${to.picture ? 'picture' : 'file'} ${n}`;
  let n = to.count + 1;
  while (taken.has(named(n).toLowerCase())) n += 1;
  return named(n);
}
